/**
 * 金额通用工具：字段识别 / 负数判定 / 负数标红
 * 约定：系统内任何金额字段，值为负数时统一用红色标出。
 */

// 负金额显示色（与 Element 的 danger 色一致）
export const MONEY_NEGATIVE_COLOR = "#f56c6c";
export const MONEY_NEGATIVE_CLASS = "money-negative";

/**
 * 金额字段白名单（按字段名分词后匹配，命中即视为金额）
 */
const MONEY_WORDS = [
	"money",
	"price",
	"amount",
	"amt",
	"balance",
	"cost",
	"bonus",
	"expense",
	"recharge",
	"consume",
	"consumption",
	"salary",
	"wage",
	"fee",
	"income",
	"profit",
	"pay",
	"payment",
	"arrears",
	"debt",
	"credit",
	"subsidy",
	"allowance",
	"deduct",
	"welfare",
	"tax",
	"fund",
	"cash",
	"settle",
	"settlement",
	"total",
	"subtotal",
	"sum",
	"capital",
	"deposit",
	"withdraw",
	"refund",
	"commission",
	"discount",
	"premium",
	"insurance"
];

/**
 * 排除词：命中即判定为非金额（优先级高于白名单）
 * 覆盖「数量/比率/状态/时间/编号」等易误伤字段，如 totalCount、discountRate、payType
 */
const EXCLUDE_WORDS = [
	"id",
	"keyid",
	"no",
	"code",
	"name",
	"title",
	"type",
	"status",
	"state",
	"is",
	"has",
	"time",
	"date",
	// 注意：month/year/day/phone/unit 等是常见修饰前缀（monthSalary、phoneBonus、unitPrice），
	// 不可作为排除词，否则会误杀真实金额字段
	"remark",
	"desc",
	"description",
	"count",
	"num",
	"number",
	"qty",
	"quantity",
	"rate",
	"ratio",
	"percent",
	"proportion",
	"method",
	"way",
	"mode",
	"img",
	"image",
	"url",
	"path",
	"file",
	"sort",
	"index",
	"level",
	"flag",
	"enable",
	"isdeleted",
	"createby",
	"updateby",
	"createid",
	"updateid",
	"userid",
	"username",
	"storeid",
	"goodsid",
	"typename",
	"statusname",
	"weight",
	"length",
	"width",
	"height",
	"size",
	"score",
	"point",
	"points",
	"version",
	"reason",
	"content",
	"address",
	"operator",
	"operation",
	"action",
	"btn",
	"icon",
	"color"
];

// 缓存，避免高频重复计算
const fieldCache = new Map();

/**
 * camelCase -> 分词，如 totalPayMoney -> ['total','pay','money']
 */
function splitWords(prop) {
	return prop
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.split(/[^a-zA-Z0-9]+/)
		.filter(Boolean)
		.map((w) => w.toLowerCase());
}

/**
 * 判断字段是否为金额字段
 * @param {string} prop 字段名，支持 a.b 形式（取最后一段）
 * @param {boolean} force 手工标注为 true 时直接判定为金额
 */
export function isMoneyField(prop, force) {
	if (force === true) return true;
	if (force === false) return false;
	if (!prop || typeof prop !== "string") return false;

	const cacheKey = prop;
	if (fieldCache.has(cacheKey)) return fieldCache.get(cacheKey);

	const words = splitWords(prop.split(".").pop());
	let result = false;
	if (words.length) {
		// 排除词优先
		if (words.some((w) => EXCLUDE_WORDS.indexOf(w) !== -1)) {
			result = false;
		} else {
			// 全等匹配（totalPayMoney 分词后为 total/pay/money）
			// 后缀匹配兜底：无分隔的小写复合名，如 totalmoney、paymoney
			result = words.some((w) => MONEY_WORDS.some((m) => w === m || w.endsWith(m)));
		}
	}
	fieldCache.set(cacheKey, result);
	return result;
}

/**
 * 清洗并转为数字，无法解析返回 null
 * 支持：数字、"￥-1,234.56 元"、"-12%"、"(100)" 括号负数、前后空格
 */
export function toNumber(val) {
	if (val === null || val === undefined || val === "") return null;
	if (typeof val === "number") return isNaN(val) ? null : val;
	if (typeof val !== "string") return null;

	let str = val.trim();
	if (!str) return null;

	// 括号负数：(100) => -100
	let bracketNegative = false;
	const bracketMatch = str.match(/^[（(]\s*([^（()）]*?)\s*[）)]$/);
	if (bracketMatch) {
		bracketNegative = true;
		str = bracketMatch[1];
	}

	// 去除货币符号、千分位、单位、空白
	str = str.replace(/[¥￥$,，\s元%％]/g, "");
	if (!str || str === "-" || str === "+") return null;

	const num = Number(str);
	if (isNaN(num)) return null;
	return bracketNegative ? -Math.abs(num) : num;
}

/**
 * 判断值是否为「负金额」
 * 安全网：字段名命中 + 值能解析为数字 + 值 < 0，三者同时满足才成立
 */
export function isNegativeMoney(val) {
	const num = toNumber(val);
	return num !== null && num < 0;
}

/**
 * 支持 a.b.c 形式取值
 */
export function getByPath(row, prop) {
	if (!row || !prop) return undefined;
	if (prop.indexOf(".") === -1) return row[prop];
	return prop.split(".").reduce((obj, key) => (obj == null ? undefined : obj[key]), row);
}

/**
 * 给 span/div 等元素用的 class 计算
 * @param {*} val 金额值
 * @param {string|boolean} field 字段名或是否强制视为金额
 */
export function moneyClass(val, field) {
	if (!isMoneyField(typeof field === "string" ? field : "", field === true)) return "";
	return isNegativeMoney(val) ? MONEY_NEGATIVE_CLASS : "";
}

/**
 * el-table 的 cell-class-name 回调
 * 自动识别金额列，负数为单元格加上标红 class（含 slot 自定义列与汇总行）
 */
export function moneyCellClass(scope) {
	if (!scope) return "";
	const column = scope.column || {};
	const row = scope.row || {};
	const prop = column.property;
	if (!prop) return "";
	if (!isMoneyField(prop)) return "";
	return isNegativeMoney(getByPath(row, prop)) ? MONEY_NEGATIVE_CLASS : "";
}

export default {
	MONEY_NEGATIVE_COLOR,
	MONEY_NEGATIVE_CLASS,
	isMoneyField,
	toNumber,
	isNegativeMoney,
	moneyClass,
	moneyCellClass,
	getByPath
};
